import { useContext, useState, useRef, useEffect } from 'react';
import type { FC, ReactNode } from 'react';
import classnames from 'classnames';
import { CssTransition } from 'sento-transition';

import Icon from 'app/components/Icon';
import triangle from 'app/icons/evolution_triangle.svg';
import { formatOdds, formatDisplayedOdds } from 'app/utils/markets';
import UserContext from 'app/common/UserContext';

import style from './style.scss';

interface Props {
  value?: number | null;
  indicatorTimeout?: number;
  className?: string;
  increaseClassName?: string;
  decreaseClassName?: string;
  competitorValue?: boolean;
  label?: ReactNode;
  evolutionFrom?: number;
  withEvolution?: boolean;
  monospace?: boolean;
  labelPlacement?: 'right' | 'left';
  noOverround?: boolean;
}

function getEvolution(
  prevValue: number | null | undefined,
  newValue: number | null | undefined,
  format: OddsFormat,
  displayedOverround: number | null,
  competitorValue: boolean
): 'increase' | 'decrease' | 'constant' {
  if (typeof prevValue !== 'number' || typeof newValue !== 'number') {
    return 'constant';
  }
  const prevValueFormatted = formatOdds(prevValue, format, displayedOverround, competitorValue);
  const newValueFormatted = formatOdds(newValue, format, displayedOverround, competitorValue);

  if (typeof prevValueFormatted !== 'number' || typeof newValueFormatted !== 'number') {
    return 'constant';
  }

  if (prevValueFormatted > newValueFormatted) {
    return 'decrease';
  }
  if (prevValueFormatted < newValueFormatted) {
    return 'increase';
  }
  return 'constant';
}

const indicatorAnimationClass = {
  enter: style.indicator_enter,
  enterActive: style.indicator_enterActive,
  exit: style.indicator_exit,
  exitActive: style.indicator_exitActive
};

const OddValue: FC<Props> = ({
  value,
  indicatorTimeout = 2000,
  className,
  increaseClassName,
  decreaseClassName,
  competitorValue = false,
  label = null,
  evolutionFrom,
  withEvolution = true,
  monospace = false,
  labelPlacement = 'left',
  noOverround = false
}) => {
  const { preferences } = useContext(UserContext);

  const [evolution, setEvolution] = useState<'increase' | 'decrease' | 'constant'>('constant');
  const currentValue = useRef(evolutionFrom ?? value);
  const [useEvolutionFrom, setUseEvolutionFrom] = useState(!!evolutionFrom);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (withEvolution) {
      const newEvolution = getEvolution(
        currentValue.current,
        value,
        preferences.odds_format,
        noOverround ? null : preferences.overround_displayed,
        competitorValue
      );
      currentValue.current = value;
      setEvolution(newEvolution);

      if (useEvolutionFrom) {
        setUseEvolutionFrom(false);
      } else {
        timeout = setTimeout(() => {
          setEvolution('constant');
        }, indicatorTimeout);
      }
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [
    value,
    competitorValue,
    preferences.odds_format,
    preferences.overround_displayed,
    withEvolution,
    noOverround
  ]);

  if (typeof value === 'number') {
    const formattedValue = formatDisplayedOdds(
      value,
      preferences.odds_format,
      noOverround ? null : preferences.overround_displayed,
      competitorValue
    );

    return (
      <div className={classnames(style.value, className)}>
        {labelPlacement === 'left' && label}
        <span className={classnames({ [style.monospace]: monospace })}>{formattedValue}</span>
        {labelPlacement === 'right' && label}
        <CssTransition
          displayed={evolution === 'increase'}
          classNames={indicatorAnimationClass}
          timeout={200}
        >
          <Icon
            className={classnames(style.indicator_increase, increaseClassName)}
            href={triangle}
          />
        </CssTransition>
        <CssTransition
          displayed={evolution === 'decrease'}
          classNames={indicatorAnimationClass}
          timeout={200}
        >
          <Icon
            className={classnames(style.indicator_decrease, decreaseClassName)}
            href={triangle}
          />
        </CssTransition>
      </div>
    );
  }

  return <div className={classnames(style.value, className)}>-</div>;
};

OddValue.displayName = 'OddValue';

export default OddValue;
